import { hoursToSeconds, millisecondsToSeconds } from 'date-fns';
import { observer } from 'mobx-react-lite';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { CommonCarousel } from '~/components/CommonCarousel';
import CheckoutProductCard from '~/pages/Checkout/components/CheckoutProductCard/CheckoutProductCard';
import type { CartItem } from '~/stores/CartStore';

import styles from './AvailableOffersForSlot.module.scss';

export const compareLeadTime = (
  deliveryMinTime: number,
  backorderLeadTime = 0,
) => {
  const now = millisecondsToSeconds(Date.now());
  const leadTime = now + hoursToSeconds(backorderLeadTime);

  return leadTime > deliveryMinTime;
};

interface AvailableOffersForSlotProps {
  currentItems: CartItem[];
  unavailableProducts: CartItem[];
}

const AvailableOffersForSlot: FC<AvailableOffersForSlotProps> = ({
  currentItems,
  unavailableProducts,
}) => {
  const { t } = useTranslation();

  if (!unavailableProducts.length) {
    return null;
  }

  const availableProductCount =
    currentItems.length - unavailableProducts.length;
  const itemLabelDeclension =
    availableProductCount > 1
      ? t('slotSelection:items', { count: availableProductCount })
      : t('slotSelection:item');

  return (
    <div className={styles.root}>
      <p className={styles.label}>
        {t('slotSelection:availableProductsForSlot', {
          expression: itemLabelDeclension,
        })}
      </p>

      <CommonCarousel
        sliderClassName={styles.slider}
        slideClassName={styles.slide}
        spaceBetween={4}
        slidesPerViewProp="auto"
      >
        {currentItems.map((item) => {
          const isUnavailable = unavailableProducts.some(
            (unavailableItem) => unavailableItem.sku === item.sku,
          );

          return (
            <CheckoutProductCard
              key={item.id}
              offer={item}
              disabled={isUnavailable}
            />
          );
        })}
      </CommonCarousel>
    </div>
  );
};

export default observer(AvailableOffersForSlot);
